import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface DashboardWsFilters {
  year: number;
  month: number;
  department: number;
  machineType: number;
  machine?: number | null;
}

@Injectable({ providedIn: 'root' })
export class OperatorDashboardSocketService implements OnDestroy {

  private ws: WebSocket | null = null;
  private currentFilters: DashboardWsFilters | null = null;

  readonly dashboardUpdate$ = new Subject<any>();
  readonly connected$ = new Subject<boolean>();

  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 1_000;
  private readonly MAX_RECONNECT_DELAY = 30_000;
  private destroyed = false;

  // ─── Public API ───────────────────────────────────────────────────────────

  subscribe(filters: DashboardWsFilters): void {
    // ✅ Reset destroyed flag — allows re-subscribing after navigating away and back
    this.destroyed = false;
    this.currentFilters = filters;

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendSubscribe(filters);
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      return;
    }

    this.connect();
  }

  /**
   * Pause the WS connection when leaving the dashboard.
   * Does NOT permanently destroy — subscribe() can revive it.
   */
  pause(): void {
    this.destroyed = true;
    this.clearTimers();
    if (this.ws) {
      this.ws.onclose = null; // prevent reconnect loop
      this.ws.close();
      this.ws = null;
    }
    this.connected$.next(false);
  }

  ngOnDestroy(): void {
    // Only called when the root injector is destroyed (app shutdown)
    this.pause();
  }

  // ─── Internal ─────────────────────────────────────────────────────────────


  // private resolveWsUrl(): string {
  //   if (typeof window === 'undefined') {
  //     return environment.WS_BASE;
  //   }
  //   const { protocol, hostname, host } = window.location;
  //   const isLocalDev =
  //       !environment.production &&
  //       (hostname === 'localhost' || hostname === '127.0.0.1');
  //   if (isLocalDev) {
  //     const wsProto = protocol === 'https:' ? 'wss:' : 'ws:';
  //     return `${wsProto}//${host}/ws`;
  //   }
  //   return environment.WS_BASE;
  // }
  private resolveWsUrl(): string {
    if (typeof window === 'undefined') {
      return environment.WS_BASE;
    }
    const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProto}//${window.location.host}/ws`;
  }

  private connect(): void {
    if (this.destroyed) return;

    const url = this.resolveWsUrl();

    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      // WebSocket constructor itself can throw in some environments
      console.warn('[OperatorDashboardWs] Failed to create WebSocket:', err);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectDelay = 1_000;
      this.connected$.next(true);
      if (url !== environment.WS_BASE) {
        console.log(`[OperatorDashboardWs] Connected via ${url} → ${environment.WS_BASE}`);
      }
      this.startPing();

      if (this.currentFilters) {
        this.sendSubscribe(this.currentFilters);
      }
    };

    this.ws.onmessage = (event: MessageEvent) => {
      this.handleMessage(event.data);
    };

    this.ws.onerror = () => {
      // onerror always precedes onclose — let onclose handle reconnect
      // Do NOT emit connected$(false) here — onclose does it
    };

    this.ws.onclose = (event: CloseEvent) => {
      this.clearPing();
      this.connected$.next(false);

      if (this.destroyed) return;

      console.warn(
          `[OperatorDashboardWs] Connection closed (code=${event.code}, url=${url}). Reconnecting in ${this.reconnectDelay}ms…`
      );

      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.MAX_RECONNECT_DELAY);
      this.connect();
    }, this.reconnectDelay);
  }

  private handleMessage(raw: string): void {
    // let msg: { event: string; data?: any; roomKey?: string };
    let msg: { event: string; data?: any; room?: string };
    try {
      msg = JSON.parse(raw);
    } catch {
      console.warn('[OperatorDashboardWs] Non-JSON message received:', raw);
      return;
    }

    switch (msg.event) {
      case 'dashboard-update':
        this.dashboardUpdate$.next(msg.data);
        break;
      case 'subscribed':
        // console.log('[OperatorDashboardWs] Subscribed to room:', msg.roomKey);
        console.log('[OperatorDashboardWs] Subscribed to room:', (msg as any).room);

        break;
      case 'pong':
        break;
      case 'error':
        console.error('[OperatorDashboardWs] Server error:', (msg as any).message);
        break;
      default:
        console.log('[OperatorDashboardWs] Unknown event:', msg.event);
    }
  }

  // private sendSubscribe(filters: DashboardWsFilters): void {
  //   this.send({ event: 'subscribe-dashboard', data: filters });
  // }

  private sendSubscribe(filters: DashboardWsFilters): void {
    this.send({
      action: 'subscribe',
      room: this.buildRoomKey(filters),
    });
  }

  private buildRoomKey(filters: DashboardWsFilters): string {

    return `${filters.year}:${filters.month}:${filters.department}:${filters.machineType}:${filters.machine ?? 'null'}`;

  }

  private send(payload: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  private startPing(): void {
    // this.clearPing();
    // this.pingInterval = setInterval(() => {
    //   this.send({ event: 'ping' });
    // }, 25_000);
  }

  private clearPing(): void {
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private clearTimers(): void {
    this.clearPing();
    if (this.reconnectTimeout !== null) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
}